import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { Composizione as DatiComposizione, FiltriComposizione, ServiceHop } from '../api/types'
import { Card, ErrorBanner, Spinner } from '../components/common'
import Composizione from '../components/Composizione'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * La composizione del servizio a partire da un indirizzo: tipicamente il VIP
 * trovato in Entry point. Qui i salti a mano vivono nella pagina finché il
 * servizio non viene creato, e passano nel servizio con la creazione.
 */
export default function ComposizioneDaIndirizzo({ seedIniziale }: { seedIniziale: string }) {
  const [seed, setSeed] = useState(seedIniziale)
  const [finestra, setFinestra] = useState('24h')
  const [profondita, setProfondita] = useState(3)
  const [fanout, setFanout] = useState(12)
  const [comuni, setComuni] = useState(false)
  const [infra, setInfra] = useState(true)
  const [salti, setSalti] = useState<ServiceHop[]>([])
  const [filtri, setFiltri] = useState<FiltriComposizione>({ nodes: [], edges: [], ports: [] })
  const [richiesta, setRichiesta] = useState<string | null>(seedIniziale.trim() || null)

  const dati = useAsync<DatiComposizione | null>(
    () =>
      richiesta
        ? api.flowComposition({
            address: richiesta,
            window: finestra,
            depth: profondita,
            fanout,
            common: comuni,
            infra,
            hops: salti,
            filters: filtri,
          })
        : Promise.resolve(null),
    [richiesta, finestra, profondita, fanout, comuni, infra, salti, filtri],
  )

  function componi() {
    const testo = seed.trim()
    if (!testo) return
    if (testo !== richiesta) {
      setSalti([])
      setFiltri({ nodes: [], edges: [], ports: [] })
    }
    setRichiesta(testo)
  }

  return (
    <>
      <Card title={t('Da quale ingresso')}>
        <div className="row">
          <div className="field">
            <label htmlFor="comp-seed">{t('Indirizzo dell’ingresso')}</label>
            <input
              id="comp-seed"
                type="text"
              value={seed}
              placeholder={t('es. 10.0.0.10')}
              onChange={(e) => setSeed(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') componi()
              }}
            />
            <p className="field__hint">
              {t('Il VIP del bilanciatore, il front-end, l’indirizzo pubblicato: da qui si seguono le chiamate verso ciò che il servizio usa.')}
            </p>
          </div>
          <div className="field" style={{ maxWidth: 170 }}>
            <label htmlFor="comp-depth">{t('Profondità')}</label>
            <select id="comp-depth" value={profondita} onChange={(e) => setProfondita(Number(e.target.value))}>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <p className="field__hint">{t('Salti di chiamata dall’ingresso.')}</p>
          </div>
          <div className="field" style={{ maxWidth: 170 }}>
            <label htmlFor="comp-fanout">{t('Chiamate per nodo')}</label>
            <input
              id="comp-fanout"
              type="number"
              min={1}
              max={40}
              value={fanout}
              onChange={(e) => setFanout(Number(e.target.value))}
            />
            <p className="field__hint">{t('Le più voluminose. Massimo 40.')}</p>
          </div>
          <div className="field" style={{ maxWidth: 190 }}>
            <label htmlFor="comp-window">{t('Finestra')}</label>
            <select id="comp-window" value={finestra} onChange={(e) => setFinestra(e.target.value)}>
              <option value="1h">{t('Ultima ora')}</option>
              <option value="24h">{t('Ultime 24 ore')}</option>
              <option value="7d">{t('Ultimi 7 giorni')}</option>
            </select>
          </div>
        </div>
        <button onClick={componi} disabled={!seed.trim()}>
          {t('Componi')}
        </button>
      </Card>

      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data && <Spinner />}

      {dati.data && richiesta && (
        <>
          <Card title={t('Composizione del servizio dietro {seed}', { seed: richiesta })}>
            <Composizione
              dati={dati.data}
              comuni={comuni}
              onComuni={setComuni}
              infra={infra}
              onInfra={setInfra}
              salti={salti}
              onAggiungiSalto={(s) => setSalti((prima) => [...prima, s])}
              onTogliSalto={(i) => setSalti((prima) => prima.filter((_, j) => j !== i))}
              filtri={filtri}
              onFiltri={setFiltri}
            />
          </Card>
          <CreaServizio seed={richiesta} profondita={profondita} salti={salti} filtri={filtri} />
        </>
      )}
    </>
  )
}

/**
 * Da una composizione a un business service: l'ingresso è l'indirizzo di
 * partenza, i salti a mano passano nel servizio, la profondità diventa quella
 * del servizio. La catena non si trascrive: il servizio la ricalcola dal
 * traffico, così resta viva.
 */
function CreaServizio({
  seed,
  profondita,
  salti,
  filtri,
}: {
  seed: string
  profondita: number
  salti: ServiceHop[]
  filtri: FiltriComposizione
}) {
  const [nome, setNome] = useState('')
  const [criticita, setCriticita] = useState('medium')
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [creato, setCreato] = useState<{ id: string; name: string } | null>(null)

  async function crea() {
    setBusy(true)
    setErrore(null)
    try {
      const servizio = await api.createService({
        name: nome.trim(),
        description: t('Creato dalla composizione di {seed}', { seed }),
        criticality: criticita,
        max_depth: profondita,
        entries: [{ ci_id: null, address: seed, port: null, protocol: null, label: null }],
        hops: salti.map((s) => ({
          from_ci_id: s.from_ci_id,
          from_address: s.from_address,
          to_ci_id: s.to_ci_id,
          to_address: s.to_address,
          port: s.port,
          note: s.note,
        })),
        composition_filters: filtri,
      })
      setCreato({ id: servizio.id, name: servizio.name })
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  if (creato) {
    return (
      <Card title={t('Business service creato')}>
        <p style={{ margin: 0 }}>
          <strong>{creato.name}</strong> —{' '}
          <Link to={`/services/${creato.id}`}>{t('Apri il servizio →')}</Link>
        </p>
      </Card>
    )
  }

  return (
    <Card title={t('Crea un business service da questa composizione')}>
      <p style={{ marginTop: 0 }} className="field__hint">
        {t('L’ingresso sarà {seed}, con la profondità scelta, i {n} salti a mano e ciò che hai tolto. La catena non si copia: il servizio la ricalcola dal traffico a ogni apertura, con le stesse esclusioni.', { seed, n: salti.length })}
      </p>
      <ErrorBanner error={errore} />
      <div className="row">
        <div className="field">
          <label htmlFor="comp-bs-nome">{t('Nome')}</label>
          <input id="comp-bs-nome" type="text" value={nome} onChange={(e) => setNome(e.target.value)} />
        </div>
        <div className="field" style={{ maxWidth: 220 }}>
          <label htmlFor="comp-bs-criticita">{t('Criticità')}</label>
          <select id="comp-bs-criticita" value={criticita} onChange={(e) => setCriticita(e.target.value)}>
            <option value="critical">{t('Critica')}</option>
            <option value="high">{t('Alta')}</option>
            <option value="medium">{t('Media')}</option>
            <option value="low">{t('Bassa')}</option>
          </select>
        </div>
      </div>
      <button onClick={crea} disabled={busy || !nome.trim()}>
        {busy ? t('Creazione…') : t('Crea il business service')}
      </button>
    </Card>
  )
}
